'use client';

import { AbTestFeatures } from '@sprd/sprd-abtest-next/types';
import { useCallback, useEffect, useRef } from 'react';
import { useReportWebVitals } from 'next/web-vitals';
import { usePathname } from '@/i18n/navigation';
import { SEARCH_WAIT_MEASURE, getSearchWaitDetail } from '@/utility/tracking/searchWaitTime';

type MetricPayload = {
  request: {
    pathname: string;
    url: string;
  };
  metrics: object;
  version?: string;
  /** @deprecated use kameleoon abTestConfig instead */
  ab_test?: string;
  abTestFeatures?: AbTestFeatures;
  application?: string;
};

type WebVitalsReportProps = {
  reportEnabled: boolean;
  reportURL: string;
  additionalData?: Partial<Omit<MetricPayload, 'request' | 'metrics'>>;
};

const sendReport = (payload: MetricPayload, reportEnabled: boolean, reportURL: string) => {
  if (reportEnabled) {
    try {
      const body = JSON.stringify(payload);
      if (navigator.sendBeacon) {
        navigator.sendBeacon(reportURL, body);
      } else {
        void fetch(reportURL, { body, method: 'POST', keepalive: true });
      }
    } catch {}
  } else {
    console.info('Web Vitals Report:', payload);
  }
};

const getReportPathname = (pathname: string) => (pathname === '/' ? '/homepage' : pathname);

export const WebVitalsTracker = ({
  reportEnabled,
  reportURL,
  additionalData,
}: WebVitalsReportProps): null => {
  const pathname = usePathname();
  // Both reporters stay subscribed across navigations, so they read the current pathname through a
  // ref instead of capturing it.
  const pathnameRef = useRef(pathname);

  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  // `useReportWebVitals` registers its web-vitals listeners again whenever the callback changes,
  // and those listeners are never removed. A callback that changed with the pathname piled up one
  // more set per navigation: every metric was sent several times, older copies with a stale
  // pathname. Keep the callback stable for the lifetime of the tracker.
  const reportWebVital = useCallback<Parameters<typeof useReportWebVitals>[0]>(
    (metric) => {
      sendReport(
        {
          ...additionalData,
          request: {
            pathname: getReportPathname(pathnameRef.current),
            url: window.location.href,
          },
          metrics: metric,
        },
        reportEnabled,
        reportURL
      );
    },
    [additionalData, reportEnabled, reportURL]
  );

  useReportWebVitals(reportWebVital);

  // Search-bar submits are client-side navigations, which the soft-navigation web vitals cannot
  // tell apart from filters, pagination or links (DEV-233277).
  useEffect(() => {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntriesByName(SEARCH_WAIT_MEASURE)) {
        const detail = getSearchWaitDetail(entry);
        if (!detail) continue;

        sendReport(
          {
            ...additionalData,
            request: {
              pathname: getReportPathname(pathnameRef.current),
              url: window.location.href,
            },
            metrics: { name: 'SEARCH_WAIT', value: entry.duration, ...detail },
          },
          reportEnabled,
          reportURL
        );
      }
    });
    observer.observe({ type: 'measure' });

    return () => observer.disconnect();
  }, [additionalData, reportEnabled, reportURL]);

  return null;
};
